import { CommonModule } from '@angular/common'
import { Router, RouterModule } from '@angular/router'
import { Component, input, inject, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { Observable } from 'rxjs'
import { IMake, IMakeResponse } from 'src/app/core/interfaces/asset.interface'
import { AssetService } from 'src/app/core/services/asset.service'
import { SearchRoutePaths } from 'src/app/core/data/search-route-paths.data'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { StorageService } from 'src/app/core/services/storage.service'
import { ColorSchemeDirective } from 'src/app/core/directives/color-scheme.directive'
import { ButtonModule } from '../button/button.module'



@Component({
  selector: 'app-browse-by-make',
  standalone: true,
  template: `
    @if (enabled()) {
      <section
        class="BrowseByMake"
        colorScheme="browseByMake.main"
      >
        <div class="container">
          <div class="BrowseByMake__divider"></div>
          <div class="BrowseByMake__heading">
            <h2 i18n="@@browse-by-make.title" class="BrowseByMake__heading__title">Browse by Make</h2>
          </div>
          <div [attr.class]="'BrowseByMake__content BrowseByMake__content--quantity-' + quantity()">
            @if (makes$ | async; as makes) {
              @for (make of makesData(); track make.id) {
                <app-button
                  [routerLink]="[SearchRoutePaths.MakeFull.replace(':make', make.name)]"
                  [attr.title]="browseMakeTitle.replace('__MAKE__', make.name)"
                  class="BrowseByMake__content__item"
                  colorScheme="browseByMake.items"
                  data-testid="make-item"
                  appearance="elevated"
                >
                @if (showImages()) {
                  <div class="BrowseByMake__content__item__image">
                    <img [src]="make.image" [attr.alt]="make.name" loading="lazy"/>
                  </div>
                } @else {
                  <div class="BrowseByMake__content__item__container">
                    <h3 class="BrowseByMake__content__item__title">
                      {{ make.name }}
                    </h3>
                    <p class="BrowseByMake__content__item__quantity" data-testid="make-quantity" [style]="styles().subtext">
                      {{ quantityAvailable.replace('__QUANTITY__', make.count.toString()) }}
                    </p>
                  </div>
                }
                </app-button>
              }
            } @else {
              @for (item of [].constructor(+quantity()); track $index) {
                <div>
                  <div
                    class="BrowseByMake__content__item spinner"
                    colorScheme="browseByMake.items"
                  >
                    <mat-spinner diameter="25"></mat-spinner>
                  </div>
                </div>
              }
            }
            <app-button
              (click)="loadAndOpenMakeFilters()"
              i18n="@@browse-by-make.view-more-link.anchor-text"
              class="BrowseByMake__content__item BrowseByMake__content__item--view-more"
              colorScheme="browseByMake.items"
              appearance="elevated"
            >View more equipment</app-button>
          </div>
        </div>
      </section>
    }
  `,
  styleUrls: ['./browse-by-make.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    ColorSchemeDirective,
    ButtonModule,
  ],
})
export class BrowseByMakeComponent {
  quantityAvailable = $localize`:@@browse-by-make.quantity-available:__QUANTITY__ available`
  browseMakeTitle = $localize`:@@browse-by-make.make-link.title:Browse __MAKE__`
  private _assetService = inject(AssetService)
  private _storageService = inject(StorageService)
  private _router = inject(Router)

  enabled = input(false)
  showImages = input(false, {
    transform: (value: any) => {
      if (value === undefined || value === null) {
        return false
      }
      return value === 'true' || value === true
    }
  })
  styles = input<any>({})
  quantity = input(5, {
    transform: (value: any) => {
      if (value === undefined || value === null) {
        return 5
      }
      return parseInt(value, 10)
    }
  })
  acceptedCount = [2, 5, 8]
  makesData = signal<IMake[]>([])
  makes$!: Observable<IMakeResponse>
  SearchRoutePaths = SearchRoutePaths
  RoutePaths = RoutePaths

  constructor() {
    toObservable(this.quantity).pipe(
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.loadMakes()
    })
  }

  loadMakes(): void {
    this.makes$ = this._assetService.getMakes()
    this.makes$.subscribe((makes) => {
      makes.results = makes.results.sort((a, b) => b.count - a.count)
      makes.results = makes.results.slice(0, this.quantity())

      if (this.acceptedCount.indexOf(makes.results.length) === -1) {
        makes.results = makes.results.slice(0, this.acceptedCount[this.acceptedCount.findIndex((count) => count > makes.results.length) - 1])
      }

      this.makesData.set(makes.results)
    })
  }

  loadAndOpenMakeFilters(): void {
    this._storageService.setItem('openMakeOnLoad', 'true', 'session')
    this._router.navigate([RoutePaths.Search])
  }
}
